import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class SpeechService {
  private isSpeaking = false;

  constructor() {}

  /**
   * Speaks the provided text using Web Speech API
   * @param text - Text to be spoken (will use fallback if undefined)
   * @param options - Optional configuration for speech
   */
  speak(text: string | undefined, options?: { rate?: number; pitch?: number; volume?: number }): void {
    // Check if browser supports speech synthesis
    if (!('speechSynthesis' in window)) {
      console.warn('Text-to-speech is not supported in this browser');
      return;
    }

    // If text is undefined or empty, don't speak
    if (!text || text.trim() === '') {
      return;
    }

    // If already speaking, cancel current speech
    if (this.isSpeaking) {
      this.stop();
    }

    // Create utterance
    const utterance = new SpeechSynthesisUtterance(text);

    // Apply options
    utterance.rate = options?.rate ?? 0.9;  // Slightly slower for clarity
    utterance.pitch = options?.pitch ?? 1;
    utterance.volume = options?.volume ?? 1;

    // Track speaking state
    utterance.onstart = () => {
      this.isSpeaking = true;
    };

    utterance.onend = () => {
      this.isSpeaking = false;
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      this.isSpeaking = false;
    };

    // Speak the text
    window.speechSynthesis.speak(utterance);
  }

  /**
   * Stops the current speech
   */
  stop(): void {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      this.isSpeaking = false;
    }
  }

  /**
   * Checks if speech is currently in progress
   */
  isSpeechActive(): boolean {
    return this.isSpeaking;
  }

  /**
   * Checks if browser supports speech synthesis
   */
  isSupported(): boolean {
    return 'speechSynthesis' in window;
  }
}
